/**
 * Charts, as inline SVG.
 *
 * No charting library, and that is a decision rather than an omission. This
 * frontend has no UI library at all and every style in it is inline; recharts
 * is around half a megabyte of JavaScript, and one screen does not justify
 * making every page carry it. Three shapes cover what the statistics page
 * actually asks for, in a couple of hundred lines that can be read.
 *
 * Everything here is presentational: no data is derived, nothing is rounded
 * except for display. A chart that computed its own totals could disagree with
 * the table beside it, which is the one thing a report must never do.
 *
 * Accessibility is not an afterthought — an SVG is invisible to a screen reader
 * unless told what it says, so every chart carries a role, a title, and a text
 * summary. The tables on the page carry the same numbers, which is the real
 * answer for anybody not seeing the picture.
 */
import { useId } from "react";

// Okabe–Ito, the palette the accessibility mode already uses: distinguishable
// under every common form of colour blindness, which a chart of five outcomes
// genuinely needs.
export const SERIES_COLOURS = [
  "#0072B2", "#009E73", "#D55E00", "#CC79A7", "#E69F00", "#56B4E9", "#F0E442",
];

function fmt(n) {
  return typeof n === "number" ? n.toLocaleString() : n;
}

export function BarChart({ data, height = 200, valueLabel = "", title }) {
  const id = useId();
  const max = Math.max(1, ...data.map(d => d.value));
  const barWidth = 100 / Math.max(data.length, 1);

  if (data.length === 0) return <Empty title={title} />;

  return (
    <figure style={{ margin: 0 }}>
      {title && <figcaption style={CAPTION}>{title}</figcaption>}
      <svg
        viewBox={`0 0 100 ${height / 3}`}
        preserveAspectRatio="none"
        role="img"
        aria-labelledby={`${id}-t`}
        style={{ width: "100%", height, display: "block" }}
      >
        <title id={`${id}-t`}>
          {data.map(d => `${d.label}: ${fmt(d.value)} ${valueLabel}`).join("; ")}
        </title>
        {data.map((d, i) => {
          const h = (d.value / max) * (height / 3);
          return (
            <rect
              key={d.label}
              x={i * barWidth + barWidth * 0.15}
              y={height / 3 - h}
              width={barWidth * 0.7}
              height={h}
              fill={d.colour || SERIES_COLOURS[i % SERIES_COLOURS.length]}
            />
          );
        })}
      </svg>
      {/* Labels in HTML rather than SVG text: the viewBox is stretched to the
          container width, which would distort any text drawn inside it. */}
      <div style={{ display: "flex", marginTop: 6 }}>
        {data.map(d => (
          <div key={d.label}
               style={{ flex: 1, textAlign: "center", fontSize: 12, color: "#374151",
                        overflowWrap: "anywhere" }}>
            <div style={{ fontWeight: 700 }}>{fmt(d.value)}</div>
            <div style={{ color: "#6b7280" }}>{d.label}</div>
          </div>
        ))}
      </div>
    </figure>
  );
}

export function DonutChart({ data, size = 180, title }) {
  const id = useId();
  const total = data.reduce((n, d) => n + d.value, 0);
  if (total === 0) return <Empty title={title} />;

  const radius = 60;
  const circumference = 2 * Math.PI * radius;
  let offset = 0;

  return (
    <figure style={{ margin: 0, display: "flex", gap: 20, alignItems: "center",
                     flexWrap: "wrap" }}>
      <svg viewBox="0 0 160 160" role="img" aria-labelledby={`${id}-t`}
           style={{ width: size, height: size, flexShrink: 0 }}>
        <title id={`${id}-t`}>
          {title ? `${title}. ` : ""}
          {data.filter(d => d.value > 0)
            .map(d => `${d.label}: ${fmt(d.value)}`).join("; ")}
        </title>
        {data.map((d, i) => {
          const fraction = d.value / total;
          const dash = fraction * circumference;
          const el = (
            <circle
              key={d.label}
              cx="80" cy="80" r={radius}
              fill="none"
              stroke={d.colour || SERIES_COLOURS[i % SERIES_COLOURS.length]}
              strokeWidth="28"
              strokeDasharray={`${dash} ${circumference - dash}`}
              strokeDashoffset={-offset}
              // Start at twelve o'clock rather than three, which is where a
              // reader expects a pie to begin.
              transform="rotate(-90 80 80)"
            />
          );
          offset += dash;
          return el;
        })}
        <text x="80" y="86" textAnchor="middle" fontSize="24" fontWeight="700" fill="#1a1a1a">
          {fmt(total)}
        </text>
      </svg>
      <div>
        {title && <div style={{ ...CAPTION, marginBottom: 8 }}>{title}</div>}
        {data.map((d, i) => (
          <div key={d.label} style={{ display: "flex", alignItems: "center", gap: 8,
                                      fontSize: 13, marginBottom: 4 }}>
            <span aria-hidden="true"
                  style={{ width: 12, height: 12, borderRadius: 3, flexShrink: 0,
                           background: d.colour || SERIES_COLOURS[i % SERIES_COLOURS.length] }} />
            <span style={{ color: "#374151" }}>{d.label}</span>
            <strong style={{ marginLeft: "auto" }}>{fmt(d.value)}</strong>
          </div>
        ))}
      </div>
    </figure>
  );
}

export function StackedBars({ data, keys, height = 24, title }) {
  const id = useId();
  if (data.length === 0) return <Empty title={title} />;

  return (
    <figure style={{ margin: 0 }}>
      {title && <figcaption style={CAPTION}>{title}</figcaption>}
      <div id={`${id}-t`} style={{ marginTop: 8 }}>
        {data.map(row => {
          const total = keys.reduce((n, k) => n + (row[k.key] || 0), 0);
          return (
            <div key={row.label} style={{ marginBottom: 10 }}>
              <div style={{ display: "flex", justifyContent: "space-between",
                            fontSize: 13, marginBottom: 3 }}>
                <span style={{ color: "#374151", overflowWrap: "anywhere" }}>{row.label}</span>
                <strong>{fmt(total)}</strong>
              </div>
              <div role="img"
                   aria-label={`${row.label}: ` + keys.map(
                     k => `${k.label} ${fmt(row[k.key] || 0)}`).join(", ")}
                   style={{ display: "flex", height, borderRadius: 4, overflow: "hidden",
                            background: "#f3f4f6" }}>
                {keys.map((k, i) => {
                  const value = row[k.key] || 0;
                  if (!value) return null;
                  return (
                    <div key={k.key}
                         title={`${k.label}: ${fmt(value)}`}
                         style={{ width: `${(value / (total || 1)) * 100}%`,
                                  background: k.colour || SERIES_COLOURS[i % SERIES_COLOURS.length] }} />
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
      <div style={{ display: "flex", gap: 14, flexWrap: "wrap", marginTop: 4 }}>
        {keys.map((k, i) => (
          <span key={k.key} style={{ display: "inline-flex", alignItems: "center", gap: 6,
                                     fontSize: 12, color: "#6b7280" }}>
            <span aria-hidden="true"
                  style={{ width: 10, height: 10, borderRadius: 2,
                           background: k.colour || SERIES_COLOURS[i % SERIES_COLOURS.length] }} />
            {k.label}
          </span>
        ))}
      </div>
    </figure>
  );
}

const CAPTION = { fontSize: 13, fontWeight: 600, color: "#374151" };

function Empty({ title }) {
  return (
    <figure style={{ margin: 0 }}>
      {title && <figcaption style={CAPTION}>{title}</figcaption>}
      <p style={{ fontSize: 13, color: "#6b7280", margin: "8px 0 0" }}>Nothing to show yet.</p>
    </figure>
  );
}
